/**
 * The copyright footnote, bottom-left corner of the bottom line - React
 * edition of ui/Copyright.ts, same reasoning intact: the build stamp is the
 * working half ("it looks wrong on my machine" is unanswerable until you
 * know whose bundle they are on), and the full/short renderings are chosen
 * by CSS media query, not script.
 *
 * The HUD's status strip shares the bottom line and starts where this ends,
 * so the footnote publishes its own width as --copyright-w. Measured with
 * useLayoutEffect on the events that can change it - language (new text),
 * resize (the media query swaps full for short) - which also fixes the old
 * "retry until connected" dance: a layout effect runs connected by
 * definition.
 */

import { useLayoutEffect, useRef } from 'react'
import ifiLogoUrl from '../../ui/assets/ifi-logo.png'
import { t } from '../../i18n/i18n'
import { useLang } from '../i18n/LanguageProvider'
import { useOpenTool } from '../state/hooks'

/** First year of the work; the range grows on its own after that. */
const SINCE = 2026

export function CopyrightFootnote() {
  const lang = useLang()
  const inTool = useOpenTool() !== null
  const rootRef = useRef<HTMLDivElement>(null)

  useLayoutEffect(() => {
    const publishWidth = (): void => {
      const root = rootRef.current
      if (!root) return
      document.documentElement.style.setProperty(
        '--copyright-w',
        `${Math.ceil(root.getBoundingClientRect().width)}px`,
      )
    }
    publishWidth()
    window.addEventListener('resize', publishWidth)
    return () => window.removeEventListener('resize', publishWidth)
  }, [lang, inTool])

  const year = new Date().getFullYear()
  const span = year > SINCE ? `${SINCE}–${year}` : `${SINCE}`

  return (
    <div ref={rootRef} className={inTool ? 'copyright in-tool' : 'copyright'}>
      {/* Decorative next to the words that say the same thing, so no alt text. */}
      <img className="copyright-logo" src={ifiLogoUrl} alt="" />
      <span className="copyright-full">
        {`©${span} ${t('name.Institute')}. ${t('footer.Built', { date: __BUILT_AT__ })}`}
      </span>
      {/* Not translated: the abbreviation is the Institute's mark, the way an
          asset tag is, and three letters survive any screen width. */}
      <span className="copyright-short">{`©${span} IFI`}</span>
    </div>
  )
}
